/**
 * JSON-LD builders. Pure functions over plain data so both SEO.jsx (runtime)
 * and scripts/prerender.js (build time) emit byte-identical structured data.
 */

import {
  SITE,
  CLINIC,
  CLINIC_ID,
  DOCTOR_ID,
  REVIEWER,
  BREADCRUMB_HOME,
  PAGE_DESCRIPTIONS,
} from "../data/seoMeta";

const CTX = "https://schema.org";

export function buildClinicSchema(lang) {
  return {
    "@context": CTX,
    "@type": ["MedicalClinic", "MedicalOrganization"],
    "@id": CLINIC_ID,
    name: CLINIC.name,
    url: SITE,
    logo: CLINIC.logo,
    image: CLINIC.image,
    telephone: CLINIC.telephone[0],
    email: CLINIC.email,
    medicalSpecialty: "Endocrinologic",
    priceRange: CLINIC.priceRange,
    currenciesAccepted: "EUR",
    paymentAccepted: "Cash, Credit Card",
    availableLanguage: CLINIC.languages,
    areaServed: [
      { "@type": "City", name: "Tallinn" },
      { "@type": "City", name: "Pärnu" },
      { "@type": "Country", name: "Estonia" },
    ],
    description: PAGE_DESCRIPTIONS.home[lang] || PAGE_DESCRIPTIONS.home.et,
    address: CLINIC.addresses.map((a) => ({
      "@type": "PostalAddress",
      streetAddress: a.streetAddress,
      addressLocality: a.addressLocality,
      postalCode: a.postalCode,
      addressCountry: "EE",
    })),
    location: CLINIC.addresses.map((a) => ({
      "@type": "Place",
      name: `${CLINIC.name} — ${a.addressLocality}`,
      hasMap: a.map,
      address: {
        "@type": "PostalAddress",
        streetAddress: a.streetAddress,
        addressLocality: a.addressLocality,
        postalCode: a.postalCode,
        addressCountry: "EE",
      },
      ...(a.geo
        ? { geo: { "@type": "GeoCoordinates", latitude: a.geo.latitude, longitude: a.geo.longitude } }
        : {}),
    })),
    openingHoursSpecification: CLINIC.openingHours.map((h) => ({
      "@type": "OpeningHoursSpecification",
      dayOfWeek: h.days,
      opens: h.opens,
      closes: h.closes,
    })),
    potentialAction: {
      "@type": "ReserveAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: CLINIC.bookingUrl,
        actionPlatform: ["http://schema.org/DesktopWebPlatform", "http://schema.org/MobileWebPlatform"],
      },
      result: { "@type": "Reservation", name: "Vastuvõtt" },
    },
    sameAs: CLINIC.sameAs,
  };
}

export function buildFaqSchema(items, lang) {
  return {
    "@context": CTX,
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.q[lang] || item.q.et,
      acceptedAnswer: { "@type": "Answer", text: item.a[lang] || item.a.et },
    })),
  };
}

/** Breadcrumb from an ordered list of { name, url } (home is prepended). */
export function buildBreadcrumbSchema(trail, lang, homeUrl) {
  const items = [{ name: BREADCRUMB_HOME[lang] || BREADCRUMB_HOME.et, url: homeUrl }, ...trail];
  return {
    "@context": CTX,
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

const LOCALE = { et: "et-EE", ru: "ru-RU", en: "en-US" };

/**
 * MedicalWebPage for patient-education articles — carries the E-E-A-T signals
 * (named author, medical reviewer, review date) that YMYL content needs.
 */
export function buildArticleSchema({ lang, canonical, article }) {
  const reviewer = {
    "@type": "Physician",
    "@id": DOCTOR_ID,
    name: REVIEWER.name,
    jobTitle: REVIEWER.jobTitle[lang] || REVIEWER.jobTitle.et,
    image: REVIEWER.image,
    medicalSpecialty: "Endocrinologic",
    worksFor: { "@id": CLINIC_ID },
  };
  return {
    "@context": CTX,
    "@type": "MedicalWebPage",
    "@id": canonical,
    url: canonical,
    mainEntityOfPage: canonical,
    headline: article.title,
    name: article.title,
    description: article.summary,
    inLanguage: LOCALE[lang] || LOCALE.et,
    ...(article.image ? { image: article.image.startsWith("http") ? article.image : `${SITE}${article.image}` } : {}),
    datePublished: article.updated,
    dateModified: article.updated,
    lastReviewed: article.updated,
    author: reviewer,
    reviewedBy: reviewer,
    publisher: { "@id": CLINIC_ID },
    medicalAudience: { "@type": "MedicalAudience", audienceType: "Patient" },
    about: { "@type": "MedicalCondition", name: article.title },
    specialty: "Endocrinologic",
    isPartOf: { "@id": CLINIC_ID },
  };
}

/** Parse "45 €" / "20€" / "alates 40 €" → 45. Returns null when not numeric. */
export function parsePrice(raw) {
  if (typeof raw !== "string") return null;
  const m = raw.replace(/\s/g, "").match(/(\d+(?:[.,]\d+)?)/);
  if (!m) return null;
  return Number(m[1].replace(",", "."));
}

/**
 * OfferCatalog for the price list — lets answer engines quote exact prices
 * ("how much is an endocrinologist appointment in Tallinn").
 */
export function buildOfferCatalogSchema({ lang, canonical, name, groups }) {
  const offers = [];
  for (const group of groups || []) {
    const groupName = group.group?.[lang] || group.group?.et || "";
    for (const item of group.items || []) {
      const price = parsePrice(typeof item.price === "string" ? item.price : item.price?.[lang]);
      if (price === null) continue;
      const itemName = item.name?.[lang] || item.name?.et;
      if (!itemName) continue;
      offers.push({
        "@type": "Offer",
        name: itemName,
        category: groupName,
        price: String(price),
        priceCurrency: "EUR",
        availability: "https://schema.org/InStock",
        url: canonical,
        itemOffered: {
          "@type": "MedicalProcedure",
          name: itemName,
          procedureType: "https://schema.org/NoninvasiveProcedure",
          howPerformed: groupName || undefined,
        },
        offeredBy: { "@id": CLINIC_ID },
        areaServed: ["Tallinn", "Pärnu"],
      });
    }
  }
  if (offers.length === 0) return null;
  return {
    "@context": CTX,
    "@type": "OfferCatalog",
    "@id": `${canonical}#prices`,
    name,
    url: canonical,
    numberOfItems: offers.length,
    itemListElement: offers,
  };
}

const LOCATION_LABEL = {
  tallinn: ["Tallinn"],
  parnu: ["Pärnu"],
  "tallinn-parnu": ["Tallinn", "Pärnu"],
};

/** Physician / Nurse entries for the team page. */
export function buildPhysiciansSchema({ lang, canonical, doctors }) {
  const list = (doctors || []).filter((d) => d && d.name);
  if (list.length === 0) return null;
  return {
    "@context": CTX,
    "@type": "ItemList",
    "@id": `${canonical}#team`,
    name: CLINIC.name,
    numberOfItems: list.length,
    itemListElement: list.map((d, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Physician",
        ...(d.id === "verner-fogel" ? { "@id": DOCTOR_ID } : {}),
        name: d.name,
        jobTitle: d.role?.[lang] || d.role?.et,
        description: d.bio?.[lang] || d.bio?.et,
        ...(d.image ? { image: d.image.startsWith("http") ? d.image : `${SITE}${d.image}` } : {}),
        medicalSpecialty: "Endocrinologic",
        worksFor: { "@id": CLINIC_ID },
        areaServed: LOCATION_LABEL[d.location] || ["Tallinn"],
        availableService: { "@type": "MedicalTherapy", name: d.role?.[lang] || d.role?.et },
      },
    })),
  };
}

export function buildWebSiteSchema(lang, homeUrl) {
  return {
    "@context": CTX,
    "@type": "WebSite",
    "@id": `${SITE}/#website`,
    url: homeUrl,
    name: CLINIC.name,
    inLanguage: LOCALE[lang] || LOCALE.et,
    publisher: { "@id": CLINIC_ID },
  };
}
